import type {
  BulkInviteRequest,
  InviteInvestorRequest,
  InvestorInviteSetting,
} from '@/store/clientsApi';
import type { AdvisorInvestorSetup } from '@/store/usersApi';

/**
 * Parameter shape for building a single-investor invite request body
 */
export interface SingleInviteParams {
  setup: AdvisorInvestorSetup;
  ccAdvisor: boolean;
  useLastName: boolean;
  signature: string;
  invitorName: string;
  sender: string; // "Custom" or a concrete name
  customSenderName?: string;
  additionalMessage?: string;
  additionalEmails?: string;
  effectiveInviteSettings?: InvestorInviteSetting;
  advisorIdFromContext: number | null;
  advisorGroupIdFromContext: number | null;
  salutationText: string;
}

/**
 * Builds the payload for inviting a single investor.
 * Extracted from component to keep UI lean and payload logic testable.
 */
export const buildSingleInviteBody = (
  p: SingleInviteParams,
): InviteInvestorRequest => {
  return {
    CCAdvisor: p.ccAdvisor,
    Salutation: p.salutationText,
    salutation: p.salutationText,
    signature: p.signature,
    InvitorName: p.invitorName,
    OtherName: p.sender === 'Custom' ? p.customSenderName : undefined,
    MultipleInviteId: null,
    InviteName: null,
    Content: p.effectiveInviteSettings?.Content ?? '',
    UseGroupName: !!p.effectiveInviteSettings?.UseGroupName,
    UseLastName: p.useLastName,
    CustomSignature: p.effectiveInviteSettings?.CustomSignature || p.signature,
    IsActive: true,
    AdvisorId: p.advisorIdFromContext,
    AdvisorGroupId: p.advisorGroupIdFromContext,
    AdvisorName: p.effectiveInviteSettings?.AdvisorName ?? '',
    AdvisorGroupName: p.effectiveInviteSettings?.AdvisorGroupName ?? '',
    LoggedInUserName: null,
    IncludeScheduleAssistance: false,
    AdvisorPermissions: null,
    AdditionalMessage: p.additionalMessage || undefined,
    AdditionalEmails: p.additionalEmails || undefined,
    InvestorId: p.setup.InvestorId,
    UserId: p.setup.UserId,
    FirstName: p.setup.FirstName,
    LastName: p.setup.LastName,
    Name: p.setup.Name,
    UserName: p.setup.UserName,
    // Additional fields mirroring legacy payload shape
    CreateDate:
      (p.setup as { CreateDate?: string | null }).CreateDate ?? undefined,
    OnboardingStep: p.setup.OnboardingStep,
    OnboardingStepDisplay: p.setup.OnboardingStepDisplay,
    CanInvite: p.setup.CanInvite,
    CanImpersonate: p.setup.CanImpersonate,
    CanDelete: p.setup.CanDelete,
    CanAssign: p.setup.CanAssign,
    IsAdminLocked: p.setup.IsAdminLocked,
    SocialSecurityNumber:
      p.setup.SocialSecurityNumber != null
        ? p.setup.SocialSecurityNumber
        : undefined,
  };
};

export interface BulkInviteParams {
  userIds: number[];
  useLastName: boolean;
  signature: string;
  invitorName: string;
  sender: string; // "Custom" or a concrete name
  customSenderName?: string;
  ccAdvisor: boolean;
  additionalMessage?: string;
  additionalEmails?: string;
  effectiveInviteSettings?: InvestorInviteSetting;
}

/**
 * Builds the payload for inviting investors in bulk.
 */
export const buildBulkInviteBody = (
  args: BulkInviteParams,
): BulkInviteRequest => {
  return {
    UserIds: args.userIds,
    UseLastName: args.useLastName,
    signature: args.signature,
    InvitorName: args.invitorName,
    OtherName: args.sender === 'Custom' ? args.customSenderName : undefined,
    CCAdvisor: args.ccAdvisor,
    // Legacy-compatible optional fields when available
    AdditionalMessage: args.additionalMessage || undefined,
    AdditionalEmails: args.additionalEmails || undefined,
    MultipleInviteId: null,
    InviteName: null,
    Content: args.effectiveInviteSettings?.Content ?? '',
    UseGroupName: !!args.effectiveInviteSettings?.UseGroupName,
    CustomSignature:
      args.effectiveInviteSettings?.CustomSignature || args.signature,
    IsActive: true,
    AdvisorId: null,
    AdvisorGroupId: null,
    AdvisorName: args.effectiveInviteSettings?.AdvisorName ?? '',
    AdvisorGroupName: args.effectiveInviteSettings?.AdvisorGroupName ?? '',
    LoggedInUserName: null,
    IncludeScheduleAssistance: false,
  };
};
